import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BadgeCheck, Pencil, Plus, ShieldCheck } from 'lucide-react';
import { Drawer, Field, QueryState, RecordTable, Select, StatusPill, todayIso, useFormErrors, type Column } from '@/components/live';
import { DropZone } from '@/components/DropZone';
import { FarmerPhoto, usePhotoUrls } from '@/components/FarmerPhoto';
import { callRpc } from '@/api/resources';
import { referenceValues } from '@/api/lookups';
import { uploadDocument } from '@/api/documents';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { can, normaliseIdentifier } from '@/auth/session';
import { toast } from '@/lib/toast';
import { isSupabaseConfigured } from '@/lib/supabase';
import { LANGUAGES, languageOption } from '@/lib/languages';
import { RELATION_TYPES, relationName } from '@/lib/relations';
import { plotLabel } from '@/lib/plotLabel';
import { ValidityBadge, addMonths } from '@/components/ValidityBadge';
import { GeographyPicker } from '@/screens/parts/GeographyPicker';
import { KycRecordDrawer } from '@/screens/parts/KycRecordDrawer';
import type { FarmerRow } from '@/types/records';

interface RepresentativeRow {
  id: string;
  representative_party_id: string;
  representative_name: string | null;
  representative_mobile: string | null;
  representative_whatsapp: string | null;
  relationship_label: string | null;
  can_acknowledge: boolean;
  can_confirm_sale_quantity: boolean;
  can_receive_payment_notification: boolean;
  consent_id: string | null;
  authorization_status: string;
  effective_from: string | null;
  effective_to: string | null;
  plot_ids: string[] | null;
  plot_codes: string | null;
  gender: string | null;
  date_of_birth: string | null;
  father_or_spouse_name: string | null;
  relation_type: string | null;
  address: string | null;
  village_id: string | null;
  village_name: string | null;
  pin_code: string | null;
  photo_document_id: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  kyc_count: number;
  kyc_verified: number;
}

const STATUS_LABEL: Record<string, string> = {
  nominated: 'Awaiting farmer\'s OTP',
  acknowledged: 'Confirmed by farmer',
  approved: 'Approved',
  revoked: 'Revoked',
  expired: 'Expired',
};

const GENDERS = [{ id: '', label: '--' }, { id: 'female', label: 'Female' }, { id: 'male', label: 'Male' }, { id: 'other', label: 'Other' }];
const SECTIONS = ['Person', 'Contact & address', 'Plots & powers'] as const;
type Section = (typeof SECTIONS)[number];

const eighteenYearsAgo = () => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().slice(0, 10); };

const EMPTY = {
  name: '', mobile: '', relationship: '', acknowledge: true, sale: false, payment: false,
  language: 'en', effective_from: todayIso(), effective_to: '',
  gender: '', date_of_birth: '', relation_type: '', father_or_spouse_name: '', whatsapp: '', address: '', village_id: '', pin_code: '',
  emergency_contact_name: '', emergency_contact_phone: '', all_plots: true, plot_ids: [] as string[],
};

/**
 * Family members and farm representatives (owner, 2026-09-21), each confirmed by the FARMER's
 * one-time code (owner: "This also requires consent for OTP validation").
 *
 * On a plot too (owner, 2026-09-28: "provision at plot level to add farm representative other than
 * owner? with same fields as like available in the worker with KYC. relationship and so on"): a
 * representative acts for all of the farmer's plots or for the ones named, carries the particulars
 * a worker carries, and has KYC records of their own (party KYC of their own party).
 *
 * Adding someone creates them as a person of their own - their mobile is theirs, never the
 * farmer's - and a pending Representative consent for the farmer. The representative stays
 * "awaiting the farmer's OTP" until the farmer acknowledges that consent with the code sent to
 * the farmer's own number; the database then authorises them. Nobody can tick them authorised.
 */
export function RepresentativesTab({ farmer, plotId }: { farmer: FarmerRow; plotId?: string }) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const mayAdd = can(profile, 'party.representative', 'create');
  const mayEdit = mayAdd || can(profile, 'party.representative', 'update');
  const reps = useRows<RepresentativeRow>('party_representatives', { filters: { party_id: farmer.party_id } });
  const plots = useRows<{ id: string; plot_code: string; plot_no: string | null; plot_name: string | null }>(
    'farm_plots', { filters: { farmer_id: farmer.id }, orderBy: { column: 'plot_code' } });
  // On a plot, the people who act for it: those named for it and those acting for every plot.
  const rows = (reps.data ?? []).filter((row) => !plotId || !(row.plot_ids ?? []).length || (row.plot_ids ?? []).includes(plotId));
  const [open, setOpen] = useState<RepresentativeRow | 'new' | null>(null);
  const [section, setSection] = useState<Section>('Person');
  const [kycFor, setKycFor] = useState<RepresentativeRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const { errors, check, clear } = useFormErrors();
  const [form, setForm] = useState({ ...EMPTY, language: farmer.preferred_language || 'en' });
  const editing = open && open !== 'new' ? open : null;
  const photos = usePhotoUrls(rows.map((row) => row.photo_document_id));
  // Authorised for the "representative" consent period by default (0075).
  const rules = useRows<{ consent_type: string; validity_months: number }>('consent_validity_rules', { filters: { consent_type: 'representative' } });
  const defaultTo = form.effective_from ? addMonths(form.effective_from, rules.data?.[0]?.validity_months ?? 36) : '';
  const relationships = useQuery({
    queryKey: ['ref', 'relationship_type'], queryFn: () => referenceValues('relationship_type'),
    enabled: isSupabaseConfigured && Boolean(open),
  });

  function start(row: RepresentativeRow | 'new') {
    clear();
    setSection('Person');
    setPhoto(null);
    setPreview(null);
    setForm(row === 'new'
      ? { ...EMPTY, language: farmer.preferred_language || 'en', all_plots: !plotId, plot_ids: plotId ? [plotId] : [] }
      : {
          ...EMPTY, name: row.representative_name ?? '', mobile: row.representative_mobile ?? '', relationship: '',
          gender: row.gender ?? '', date_of_birth: row.date_of_birth ?? '', relation_type: row.relation_type ?? '',
          father_or_spouse_name: row.father_or_spouse_name ?? '', whatsapp: '', address: row.address ?? '',
          village_id: row.village_id ?? '', pin_code: row.pin_code ?? '', emergency_contact_name: row.emergency_contact_name ?? '',
          emergency_contact_phone: row.emergency_contact_phone ?? '', all_plots: !(row.plot_ids ?? []).length, plot_ids: row.plot_ids ?? [],
        });
    setOpen(row);
  }

  const columns: Array<Column<RepresentativeRow>> = [
    { key: 'representative_name', label: 'Name', render: (row) => (
      <span className="cell-stack">
        <span className="icon-text">
          <FarmerPhoto name={row.representative_name ?? ''} size={28} url={row.photo_document_id ? photos.data?.[row.photo_document_id] : null} />
          <strong>{row.representative_name ?? '--'}</strong>
        </span>
        <small className="muted">{relationName(row.relation_type, row.father_or_spouse_name) ?? ''}</small>
      </span>
    ) },
    { key: 'relationship_label', label: 'Relationship', render: (row) => row.relationship_label ?? '--' },
    { key: 'representative_mobile', label: 'Mobile', render: (row) => row.representative_mobile ?? '--' },
    { key: 'plot_codes', label: 'Plots', render: (row) => row.plot_codes ?? 'All plots' },
    { key: 'kyc_count', label: 'KYC', render: (row) => (
      <button className="link-button" onClick={() => setKycFor(row)} type="button">
        <BadgeCheck size={14} /> {row.kyc_count ? `${row.kyc_verified} of ${row.kyc_count} verified` : 'Add KYC'}
      </button>
    ) },
    { key: 'effective_to', label: 'Authorised', render: (row) => <ValidityBadge from={row.effective_from} to={row.effective_to} /> },
    {
      key: 'powers', label: 'May',
      render: (row) => [
        row.can_acknowledge && 'acknowledge',
        row.can_confirm_sale_quantity && 'confirm sale quantity',
        row.can_receive_payment_notification && 'receive payment notices',
      ].filter(Boolean).join(', ') || '--',
    },
    {
      key: 'authorization_status', label: 'Status',
      render: (row) => (
        <span className="rep-status">
          <StatusPill value={row.authorization_status} />
          <small className="muted">{STATUS_LABEL[row.authorization_status] ?? ''}</small>
        </span>
      ),
    },
    {
      key: 'act', label: '',
      render: (row) => (
        <span className="icon-text">
          {mayEdit && (
            <button aria-label="Edit particulars" className="icon-only" onClick={() => start(row)} title="Edit particulars" type="button"><Pencil size={15} /></button>
          )}
          {row.authorization_status === 'nominated' && (
            <Link className="icon-text" title="The farmer confirms with the code sent to their own number" to="/consent-capture">
              <ShieldCheck size={15} /> Get farmer's OTP
            </Link>
          )}
        </span>
      ),
    },
  ];

  async function save() {
    const mobile = normaliseIdentifier('sms', form.mobile.trim());
    const whatsapp = form.whatsapp.trim() ? normaliseIdentifier('whatsapp', form.whatsapp.trim()) : null;
    const emergency = form.emergency_contact_phone.trim() ? normaliseIdentifier('sms', form.emergency_contact_phone.trim()) : null;
    const plotIds = form.all_plots ? [] : form.plot_ids;
    const problems = {
      name: editing || form.name.trim() ? undefined : 'Give the person\'s name.',
      mobile: editing || !('error' in mobile) ? undefined : mobile.error,
      relationship: editing || form.relationship ? undefined : 'Choose the relationship.',
      date_of_birth: form.date_of_birth && form.date_of_birth > eighteenYearsAgo() ? 'A representative must be 18 or over.' : undefined,
      whatsapp: whatsapp && 'error' in whatsapp ? whatsapp.error : undefined,
      emergency_contact_phone: emergency && 'error' in emergency ? emergency.error : undefined,
      pin_code: form.pin_code && !/^[1-9]\d{5}$/.test(form.pin_code.trim()) ? 'A PIN code is 6 digits.' : undefined,
      plot_ids: !form.all_plots && plotIds.length === 0 ? 'Choose the plots they act for, or all plots.' : undefined,
      effective_to: editing || (form.effective_to || defaultTo) > form.effective_from ? undefined : 'The authorisation must end after it starts.',
    };
    if (!check(problems)) {
      const firstIn = (keys: string[]) => keys.some((key) => (problems as Record<string, string | undefined>)[key]);
      setSection(firstIn(['name', 'relationship', 'date_of_birth']) ? 'Person' : firstIn(['mobile', 'whatsapp', 'pin_code', 'emergency_contact_phone']) ? 'Contact & address' : 'Plots & powers');
      return;
    }
    setSaving(true);
    try {
      let repId = editing?.id ?? null;
      if (!repId) {
        repId = await callRpc<string>('fn_add_representative', {
          p_farmer_party_id: farmer.party_id,
          p_name: form.name.trim(),
          p_mobile: 'value' in mobile ? mobile.value : null,
          p_relationship_type_id: form.relationship,
          p_can_acknowledge: form.acknowledge,
          p_can_confirm_sale: form.sale,
          p_can_receive_payment: form.payment,
          p_language_code: form.language,
        });
        await callRpc('fn_set_representative_validity', {
          p_representative_id: repId, p_effective_from: form.effective_from, p_effective_to: form.effective_to || defaultTo,
        });
      }
      let photoId: string | null = null;
      if (photo && profile?.organization) {
        try {
          photoId = (await uploadDocument({ file: photo, bucket: 'farmer-photos', organizationId: profile.organization.id, ownerId: repId })).id;
        } catch (error) {
          toast.error(`Saved, but the photo was not: ${error instanceof Error ? error.message : 'upload failed'}.`);
        }
      }
      await callRpc('fn_save_representative_details', {
        p_representative_id: repId,
        p_plot_ids: plotIds,
        p_gender: form.gender || null,
        p_date_of_birth: form.date_of_birth || null,
        p_father_or_spouse_name: form.father_or_spouse_name.trim() || null,
        p_relation_type: form.relation_type || null,
        p_address: form.address.trim() || null,
        p_village_id: form.village_id || null,
        p_pin_code: form.pin_code.trim() || null,
        p_photo_document_id: photoId,
        // Blank keeps what is on file when editing: the list shows it masked, so it is not refilled.
        p_whatsapp_number: whatsapp && 'value' in whatsapp ? whatsapp.value : null,
        p_emergency_contact_name: form.emergency_contact_name.trim() || null,
        p_emergency_contact_phone: emergency && 'value' in emergency ? emergency.value : null,
      });
      await queryClient.invalidateQueries({ queryKey: ['party_representatives'] });
      await queryClient.invalidateQueries({ queryKey: ['party_consents'] });
      toast.success(editing ? 'Saved.' : 'Added. The farmer now confirms with the OTP sent to their own number (Consent capture). Add their KYC from the list.');
      setOpen(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  const togglePlot = (id: string, on: boolean) => setForm({ ...form, plot_ids: on ? [...form.plot_ids, id] : form.plot_ids.filter((entry) => entry !== id) });

  return (
    <>
      <div className="toolbar-row">
        <span className="muted">
          {plotId ? 'People who may act for this plot besides its owner: named for it, or for all the farmer\'s plots.' : 'People who may act for this farmer.'}
          {' '}Each is confirmed by the farmer's own OTP.
        </span>
        <button className="primary" disabled={!mayAdd} onClick={() => start('new')}
                title={mayAdd ? undefined : 'Your role cannot add representatives.'} type="button">
          <Plus size={16} /> {plotId ? 'Add plot representative' : 'Add family member / representative'}
        </button>
      </div>
      <QueryState empty={plotId ? 'No representative for this plot yet.' : 'No family members or representatives yet.'} query={reps}>
        <RecordTable columns={columns} rows={rows} />
      </QueryState>

      <Drawer
        footer={
          <>
            <button className="cancel" onClick={() => setOpen(null)} type="button">Cancel</button>
            <button className="primary" disabled={saving} onClick={() => void save()} type="button">
              {saving ? 'Saving...' : editing ? 'Save changes' : 'Add and send for consent'}
            </button>
          </>
        }
        onClose={() => setOpen(null)}
        open={Boolean(open)}
        subtitle={editing ? `${editing.representative_name ?? ''} for ${farmer.full_name}.` : `For ${farmer.full_name}. Confirmed only when the farmer gives the OTP.`}
        title={editing ? 'Representative particulars' : plotId ? 'Add plot representative' : 'Add family member / representative'}
      >
        <div className="form-tabs" role="tablist" aria-label="Form sections">
          {SECTIONS.map((name) => (
            <button aria-selected={name === section} className={name === section ? 'active' : ''} key={name}
                    onClick={() => setSection(name)} role="tab" type="button">{name}</button>
          ))}
        </div>

        {section === 'Person' && (
          <>
            <div className="form-photo">
              <FarmerPhoto name={form.name} size={72}
                           url={preview ?? (editing?.photo_document_id ? photos.data?.[editing.photo_document_id] : null)} />
              <div className="form-photo-actions">
                <DropZone accept="image/*" buttonLabel={preview || editing?.photo_document_id ? 'Change photo' : 'Add photo'}
                          label="Drag a photo here, or"
                          onFile={(file) => { setPhoto(file); setPreview((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(file); }); }} />
              </div>
            </div>
            <Field error={errors.name} label="Name" required={!editing}>
              <input disabled={Boolean(editing)} onChange={(event) => setForm({ ...form, name: event.target.value })} value={form.name} />
            </Field>
            {!editing && (
              <Field error={errors.relationship} hint="How they are related to the farmer, or their role on the farm." label="Relationship" required>
                <Select onChange={(value) => setForm({ ...form, relationship: value })}
                        options={(relationships.data ?? []).map((row) => ({ id: row.id, label: row.label }))}
                        placeholder={relationships.isLoading ? 'Loading...' : 'Select relationship'} value={form.relationship} />
              </Field>
            )}
            <div className="field-row">
              <Field label="Gender"><Select onChange={(value) => setForm({ ...form, gender: value })} options={GENDERS} value={form.gender} /></Field>
              <Field error={errors.date_of_birth} hint="18 or over only." label="Date of birth">
                <input max={eighteenYearsAgo()} onChange={(event) => setForm({ ...form, date_of_birth: event.target.value })} type="date" value={form.date_of_birth} />
              </Field>
            </div>
            <div className="field-row">
              <Field hint="As printed on the ID." label="Relation">
                <Select onChange={(value) => setForm({ ...form, relation_type: value })} options={RELATION_TYPES} value={form.relation_type} />
              </Field>
              <Field hint="As written on the identity document." label="Father's or spouse's name">
                <input onChange={(event) => setForm({ ...form, father_or_spouse_name: event.target.value })} value={form.father_or_spouse_name} />
              </Field>
            </div>
            {editing && <p className="muted">KYC documents are added from the list: KYC on this person's row.</p>}
          </>
        )}

        {section === 'Contact & address' && (
          <>
            <div className="field-row">
              <Field error={errors.mobile} hint={editing ? 'Their registered mobile; it is theirs, never the farmer\'s.' : 'Their own number - never the farmer\'s.'} label="Mobile" required={!editing}>
                <input disabled={Boolean(editing)} inputMode="tel" onChange={(event) => setForm({ ...form, mobile: event.target.value })}
                       placeholder="98xxxxxx01" value={form.mobile} />
              </Field>
              <Field error={errors.whatsapp} hint={editing && editing.representative_whatsapp ? `On file: ${editing.representative_whatsapp}. Blank keeps it.` : 'Blank: none.'} label="WhatsApp number">
                <input inputMode="tel" onChange={(event) => setForm({ ...form, whatsapp: event.target.value })} placeholder="98xxxxxx01" value={form.whatsapp} />
              </Field>
            </div>
            <GeographyPicker onChange={(villageId) => setForm({ ...form, village_id: villageId })} value={form.village_id} />
            <Field label="Address">
              <textarea onChange={(event) => setForm({ ...form, address: event.target.value })} rows={2} value={form.address} />
            </Field>
            <Field error={errors.pin_code} label="PIN code">
              <input inputMode="numeric" maxLength={6} onChange={(event) => setForm({ ...form, pin_code: event.target.value })} value={form.pin_code} />
            </Field>
            <div className="field-row">
              <Field label="Emergency contact"><input onChange={(event) => setForm({ ...form, emergency_contact_name: event.target.value })} value={form.emergency_contact_name} /></Field>
              <Field error={errors.emergency_contact_phone} label="Emergency phone">
                <input inputMode="tel" onChange={(event) => setForm({ ...form, emergency_contact_phone: event.target.value })} value={form.emergency_contact_phone} />
              </Field>
            </div>
            {!editing && (
              <Field hint="Language of the consent the farmer confirms." label="Consent language">
                <Select onChange={(value) => setForm({ ...form, language: value })}
                        options={LANGUAGES.map((language) => languageOption(language.code))} value={form.language} />
              </Field>
            )}
          </>
        )}

        {section === 'Plots & powers' && (
          <>
            <Field error={errors.plot_ids} label="Acts for">
              <div aria-label="Plots" className="check-list is-stacked" role="group">
                <label>
                  <input checked={form.all_plots} onChange={(event) => setForm({ ...form, all_plots: event.target.checked })} type="checkbox" />
                  All of {farmer.full_name}&apos;s plots
                </label>
                {!form.all_plots && (plots.data ?? []).map((plot) => (
                  <label key={plot.id}>
                    <input checked={form.plot_ids.includes(plot.id)} onChange={(event) => togglePlot(plot.id, event.target.checked)} type="checkbox" />
                    {plotLabel(plot)}
                  </label>
                ))}
              </div>
            </Field>
            {!editing && (
              <>
                <fieldset className="check-group">
                  <legend>May act for the farmer to</legend>
                  <label className="check-row"><input checked={form.acknowledge} onChange={(e) => setForm({ ...form, acknowledge: e.target.checked })} type="checkbox" /> Acknowledge notices and documents</label>
                  <label className="check-row"><input checked={form.sale} onChange={(e) => setForm({ ...form, sale: e.target.checked })} type="checkbox" /> Confirm sale quantities</label>
                  <label className="check-row"><input checked={form.payment} onChange={(e) => setForm({ ...form, payment: e.target.checked })} type="checkbox" /> Receive payment notifications</label>
                </fieldset>
                <div className="field-row">
                  <Field label="Authorised from" required>
                    <input onChange={(event) => setForm({ ...form, effective_from: event.target.value })} type="date" value={form.effective_from} />
                  </Field>
                  <Field error={errors.effective_to} hint={`Default: ${rules.data?.[0]?.validity_months ?? 36} months.`} label="Authorised until" required>
                    <input min={form.effective_from} onChange={(event) => setForm({ ...form, effective_to: event.target.value })} type="date"
                           value={form.effective_to || defaultTo} />
                  </Field>
                </div>
              </>
            )}
          </>
        )}
      </Drawer>

      {kycFor && (
        <KycRecordDrawer onClose={() => { setKycFor(null); void queryClient.invalidateQueries({ queryKey: ['party_representatives'] }); }}
                         open partyId={kycFor.representative_party_id} partyLabel={kycFor.representative_name ?? 'Representative'} roleContext="representative" />
      )}
    </>
  );
}
